import React from 'react';
import {
  Field,
  MiniSelect,
  NumberInput,
  BiometryList,
  InlineNumber,
  SectionHeading,
  TwoColumnLayout,
  ScreeningSection,
} from './primitives';
import { T1_BIOMETRY, OPTIONS } from './lib/form-config';
import { useReportData } from './lib/report-context';

function ViabilitySection() {
  const { fetalAssessment, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;
  const hr = fetalAssessment.heartRate
    ? Math.round(fetalAssessment.heartRate)
    : undefined;

  return (
    <>
      <SectionHeading>sections.viabilityLocation</SectionHeading>
      <div className="grid grid-cols-2 gap-1">
        <Field label="fields.pregnancyLocation">
          <MiniSelect
            defaultValue={(sr.pregnancy_location as string) || ''}
            placeholder="placeholders.selectLocation"
            options={[
              { value: 'INTRA_UTERINE', label: 'options.intraUterine' },
              { value: 'ECTOPIC_TUBAL', label: 'options.tubalEctopic' },
              { value: 'ECTOPIC_OVARIAN', label: 'options.ovarianEctopic' },
              { value: 'ECTOPIC_CERVICAL', label: 'options.cervicalEctopic' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ pregnancy_location: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.numberOfEmbryos">
          <NumberInput
            defaultValue={
              sr.nb_embryos != null ? Number(sr.nb_embryos) : undefined
            }
            placeholder="0"
            min={0}
            max={4}
            step={1}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                nb_embryos: e.target.value ? parseInt(e.target.value) : null,
              })
            }
          />
        </Field>
        <Field label="fields.gestationalSac">
          <MiniSelect
            defaultValue={(sr.gestational_sac_visible as string) || ''}
            placeholder="placeholders.selectSac"
            options={[
              { value: 'yes', label: 'options.visible' },
              { value: 'no', label: 'options.notVisible' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                gestational_sac_visible: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.msd">
          <NumberInput
            defaultValue={
              sr.gestational_sac_msd_mm != null
                ? Number(sr.gestational_sac_msd_mm)
                : undefined
            }
            placeholder="mm"
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                gestational_sac_msd_mm: e.target.value
                  ? parseFloat(e.target.value)
                  : null,
              })
            }
          />
        </Field>
        <Field label="fields.yolkSac">
          <MiniSelect
            defaultValue={(sr.yolk_sac_visible as string) || ''}
            placeholder="placeholders.selectYolkSac"
            options={[
              { value: 'present-normal', label: 'options.presentNormal' },
              { value: 'large', label: 'options.presentGt6mm' },
              { value: 'calcified', label: 'options.calcified' },
              { value: 'absent', label: 'options.absent' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                yolk_sac_visible: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.embryoVisible">
          <MiniSelect
            defaultValue={
              sr.embryo_visible === true
                ? 'yes'
                : sr.embryo_visible === false
                  ? 'no'
                  : ''
            }
            placeholder="placeholders.selectEmbryoVisible"
            options={[
              { value: 'yes', label: 'options.yes' },
              { value: 'no', label: 'options.no' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                embryo_visible:
                  e.target.value === 'yes'
                    ? true
                    : e.target.value === 'no'
                      ? false
                      : null,
              })
            }
          />
        </Field>
        <Field label="fields.cardiacActivity" explainFindingKey="cardiac_activity">
          <MiniSelect
            defaultValue={
              sr.cardiac_activity_present === true
                ? 'present'
                : sr.cardiac_activity_present === false
                  ? 'absent'
                  : ''
            }
            placeholder="placeholders.selectCardiacActivity"
            options={[
              { value: 'present', label: 'options.present' },
              { value: 'absent', label: 'options.absent' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                cardiac_activity_present:
                  e.target.value === 'present'
                    ? true
                    : e.target.value === 'absent'
                      ? false
                      : null,
              })
            }
          />
        </Field>
        <Field label="fields.heartRate">
          <NumberInput
            defaultValue={hr}
            placeholder="BPM"
            step={1}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                heart_rate: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
          />
        </Field>
      </div>
    </>
  );
}

function BiometrySection() {
  const { measurementValues, studyReportFields, onUpdateFindings } =
    useReportData();
  const sr = studyReportFields;

  const ntMom =
    measurementValues.nt_mom != null
      ? Number(measurementValues.nt_mom)
      : undefined;
  const nasalBone =
    measurementValues.nasal_bone_t1_present === true
      ? 'present'
      : measurementValues.nasal_bone_t1_present === false
        ? 'absent'
        : '';
  const hardPalate =
    measurementValues.hard_palate_t1_intact === true
      ? 'normal'
      : measurementValues.hard_palate_t1_intact === false
        ? 'interrupted'
        : '';

  const refChart =
    measurementValues.reference_chart != null
      ? String(measurementValues.reference_chart)
      : '';

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <SectionHeading>sections.biometryDating</SectionHeading>
        <MiniSelect
          defaultValue={refChart || 'intergrowth-21st'}
          options={OPTIONS.referenceChart}
          className="w-44 shrink-0"
          onChange={(e) =>
            onUpdateFindings?.({ reference_chart: e.target.value || null })
          }
        />
      </div>
      <BiometryList fields={T1_BIOMETRY} />
      <InlineNumber
        label="fields.ntMom"
        placeholder="1.0"
        step={0.01}
        defaultValue={ntMom}
        inputClassName="w-16"
        onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
          onUpdateFindings?.({
            nt_mom: e.target.value ? parseFloat(e.target.value) : null,
          })
        }
      />
      <div className="grid grid-cols-2 gap-1 pt-1">
        <Field label="fields.nasalBone">
          <MiniSelect
            defaultValue={nasalBone}
            placeholder="placeholders.selectNasalBone"
            options={[
              { value: 'present', label: 'options.present' },
              { value: 'absent', label: 'options.absent' },
              { value: 'not-seen', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                nasal_bone_t1_present:
                  e.target.value === 'present'
                    ? true
                    : e.target.value === 'absent'
                      ? false
                      : null,
              })
            }
          />
        </Field>
        <Field label="fields.hardPalate">
          <MiniSelect
            defaultValue={hardPalate}
            placeholder="placeholders.selectHardPalate"
            options={[
              { value: 'normal', label: 'options.normalContinuous' },
              { value: 'interrupted', label: 'options.interruptedAbsent' },
              { value: 'not-seen', label: 'options.notVisualized' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                hard_palate_t1_intact:
                  e.target.value === 'normal'
                    ? true
                    : e.target.value === 'interrupted'
                      ? false
                      : null,
              })
            }
          />
        </Field>
        <Field label="fields.subMandibularSpace">
          <MiniSelect
            defaultValue={(sr.submandibular_space as string) || ''}
            placeholder="placeholders.selectSubMandibular"
            options={[
              { value: 'clear', label: 'options.clearNormal' },
              { value: 'solid', label: 'options.solidEchoes' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                submandibular_space: e.target.value || null,
              })
            }
          />
        </Field>
      </div>
    </>
  );
}

function UterusSection() {
  const { studyReportFields, onUpdateFindings } = useReportData();
  const sr = studyReportFields;

  return (
    <>
      <SectionHeading>sections.uterusAdnexa</SectionHeading>
      <div className="grid grid-cols-2 gap-1">
        <Field label="fields.uterusOvaries">
          <MiniSelect
            defaultValue={(sr.uterus_ovaries as string) || ''}
            options={[
              { value: 'normal', label: 'options.normal' },
              { value: 'fibroids', label: 'options.fibroids' },
              { value: 'cyst', label: 'options.ovarianCyst' },
              { value: 'malformation', label: 'options.malformation' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({ uterus_ovaries: e.target.value || null })
            }
          />
        </Field>
        <Field label="fields.subchorionicHematoma">
          <MiniSelect
            defaultValue={(sr.subchorionic_hematoma as string) || ''}
            options={[
              { value: 'absent', label: 'options.absent' },
              { value: 'present', label: 'options.present' },
            ]}
            onChange={(e) =>
              onUpdateFindings?.({
                subchorionic_hematoma: e.target.value || null,
              })
            }
          />
        </Field>
        <Field label="fields.hematomaVolume">
          <NumberInput
            defaultValue={
              sr.subchorionic_hematoma_cm3 != null
                ? Number(sr.subchorionic_hematoma_cm3)
                : undefined
            }
            placeholder="cm³"
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                subchorionic_hematoma_cm3: e.target.value
                  ? parseFloat(e.target.value)
                  : null,
              })
            }
          />
        </Field>
      </div>
    </>
  );
}

export function T1Form() {
  const { reportDetailMode } = useReportData();
  const isFullReport = reportDetailMode === 'full';

  return (
    <TwoColumnLayout
      left={
        <>
          <ViabilitySection />
          <ScreeningSection />
        </>
      }
      right={
        <>
          <BiometrySection />
          {/* <SectionHeading>sections.earlyMorphology</SectionHeading> */}
          {/* <MorphologyList items={T1_MORPHOLOGY} /> */}
          {isFullReport && <UterusSection />}
        </>
      }
    />
  );
}
